import React, { useEffect, useState } from "react";

import { useMsal } from "@azure/msal-react";

import { Card } from "@/components/ui/card";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as PreviewIcon } from "@assets/svg/eye-filled.svg";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";

import { DOCUMENT_CLASSIFICATIONS } from "@constants/documents";

import { getFileUrl } from "@utils/file";

import { MergeFilesList } from "./MergeFilesList";
import { MergedFilePreview } from "./MergedFilePreview";

type MergeDocumentsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedFiles: DocSorterFileSchema[];
  onMerge: (category: string, orderedFileIds: string[]) => void;
  isLoading?: boolean;
};

export const MergeDocumentsModal = ({
  isOpen,
  onClose,
  selectedFiles,
  onMerge,
  isLoading = false,
}: MergeDocumentsModalProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [orderedFiles, setOrderedFiles] = useState<DocSorterFileSchema[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<
    Record<string, { url: string; type: string }>
  >({});
  const { accounts } = useMsal();

  // Initialize ordered files when modal opens or selectedFiles change
  useEffect(() => {
    if (isOpen) {
      setOrderedFiles([...selectedFiles]);
    }
  }, [isOpen, selectedFiles]);

  // Fetch preview URLs for all files
  useEffect(() => {
    if (showPreview && orderedFiles.length > 0) {
      const fetchUrls = async () => {
        const data: Record<string, { url: string; type: string }> = {};
        for (const file of orderedFiles) {
          try {
            const result = await getFileUrl(file);
            if (result.url && result.type) {
              data[file.id] = { url: result.url, type: result.type };
            }
          } catch (error) {
            console.error(`Failed to fetch URL for file ${file.id}:`, error);
          }
        }
        setPreviewData(data);
      };
      fetchUrls();
    }
  }, [showPreview, orderedFiles, accounts]);

  const handleMerge = () => {
    if (selectedCategory) {
      const orderedFileIds = orderedFiles.map((f) => f.id);
      onMerge(selectedCategory, orderedFileIds);
      setSelectedCategory("");
      setShowPreview(false);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedCategory("");
    setShowPreview(false);
    setPreviewData({});
    onClose();
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newFiles = [...orderedFiles];
    const draggedFile = newFiles[draggedIndex];
    newFiles.splice(draggedIndex, 1);
    newFiles.splice(index, 0, draggedFile);

    setOrderedFiles(newFiles);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = orderedFiles.filter((_, i) => i !== index);
    setOrderedFiles(newFiles);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="flex max-h-[90vh] min-w-[900px] max-w-4xl flex-col gap-2 overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            Merge Documents
          </DialogTitle>
          <DialogDescription>
            {showPreview
              ? "Review the merge details before confirming. This will create a new document with the following files merged in order."
              : "Drag and drop to reorder the documents to be merged."}
          </DialogDescription>
        </DialogHeader>

        {/* Drag-and-drop Files List */}
        <div className="flex-shrink-0">
          {!showPreview && (
            <div className="max-h-[300px] overflow-y-auto pr-1">
              <MergeFilesList
                files={orderedFiles}
                mode="edit"
                draggedIndex={draggedIndex}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onRemove={handleRemoveFile}
              />
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-8 overflow-y-auto">
          {/* Category Selection */}
          <Card className="gap-2 p-4">
            <label className="text-sm font-medium">
              Selected Category <span className="text-red-500">*</span>
            </label>
            {!showPreview && (
              <p className="text-muted-foreground mb-2 text-xs">
                Select one of the {DOCUMENT_CLASSIFICATIONS.length} categories from
                dropdown taken on merged file
              </p>
            )}
            <Select
              disabled={showPreview}
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {DOCUMENT_CLASSIFICATIONS.map((classification) => (
                  <SelectItem
                    key={classification.id}
                    value={classification.category}
                  >
                    {classification.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>

          {/* Preview Files List */}
          <div className="flex-shrink-0">
            {showPreview && (
              <div className="max-h-[300px] overflow-y-auto pr-1">
                <label className="text-sm font-medium">
                  Documents to merge (in order):
                </label>
                <MergeFilesList files={orderedFiles} mode="preview" />
              </div>
            )}
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="flex-shrink-0 rounded-xl bg-accent-50 p-3">
              <Accordion type="single" collapsible defaultValue="preview-1">
                <AccordionItem value="preview-1">
                  <AccordionTrigger className="py-2 text-sm text-foreground">
                    <div className="flex items-center gap-2">
                      <PreviewIcon className="size-4" /> Merged Documents Preview
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="border-t border-border pt-2">
                    <div className="h-[400px] overflow-hidden">
                      <MergedFilePreview
                        files={orderedFiles
                          .map((file) => {
                            const data = previewData[file.id];
                            if (!data) return null;
                            return {
                              id: file.id,
                              name: file.name,
                              url: data.url,
                              type: data.type,
                            };
                          })
                          .filter((f): f is NonNullable<typeof f> => f !== null)}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2">
          {showPreview ? (
            <>
              <Button
                variant="outline"
                onClick={() => setShowPreview(false)}
                disabled={isLoading}
              >
                Back to Edit
              </Button>
              <Button onClick={handleMerge} disabled={isLoading}>
                {isLoading ? "Merging..." : "Confirm Merge"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={() => setShowPreview(true)}
                disabled={!selectedCategory || orderedFiles.length === 0}
              >
                Preview Merge
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
